import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Offcanvas, Tooltip } from 'bootstrap-italia';
import { StateManagementService } from 'src/app/services/state-management.service';
import { SelectedFeatureState } from 'src/app/interface/interface';
import {
  phaseOfWorkState,
  normalizeWorkState,
  PHASE_LABEL,
  PHASE_BAR_CLASS,
  PHASE_NUMBER,
} from 'src/app/shared/work-state';

type CriticalLevel = 'Alta' | 'Media' | 'Bassa';

interface DonutConfig {
  value: number;
  class: string;
}

@Component({
  selector: 'app-anagrafica-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './anagrafica-modal.component.html',
  styleUrls: ['./anagrafica-modal.component.css'],
})
export class AnagraficaModalComponent implements OnInit {
  // UI state
  changeInfo = false;
  activeTab: string = 'avanzamento';

  // Dati
  attributes: any = null;
  documents: any[] = [];
  folders: any[] = [];
  phases: any[] = [];
  metricCalculations: any[] = [];
  assets: any[] = [];
  criticals: any[] = [];

  //fase corrente (derivata DA wr_work_state)
  currentPhaseUi: { number: string; title: string } | null = null;

  siteImages: Record<string, string[]> = {
    dotazioni: ['assets/sitesPhotos/dotazioni.jpg'],
    agrometeo: ['assets/sitesPhotos/rete agrometeo.jpg'],
    dinamicanazionale: ['assets/sitesPhotos/rete dinamica nazionale.jpg'],
    franeinsitu: ['assets/sitesPhotos/rete frane.jpg'],
    ghg: ['assets/sitesPhotos/rete ghg.jpg'],
    gravimetrica: ['assets/sitesPhotos/rete gravimetrica.jpg'],
    idrometeo: ['assets/sitesPhotos/rete idrometeo.jpg'],
    incendio: ['assets/sitesPhotos/rete incendio.jpg'],
    livellazione: ['assets/sitesPhotos/rete livellazione.jpg'],
    reteradar: ['assets/sitesPhotos/reteradar.jpg'],
    sismica: ['assets/sitesPhotos/rete sismica.jpg'],
  };

  critical?: CriticalLevel;

  DONUT_CONFIG: Record<CriticalLevel, DonutConfig> = {
    Alta: { value: 100, class: 'donut-red' },
    Media: { value: 100, class: 'donut-yellow' },
    Bassa: { value: 100, class: 'donut-green' },
  };

  currentImages: string[] = [];
  private offcanvasInstance: Offcanvas | null = null;

  get phaseSegments(): any[] {
    const current = this.getCurrentPhaseNumber() ?? 0;
    const backend = this.getPhasesOrdered().length;

    return Array.from({
      length: Math.max(current, backend, 5) // 5 = numero fasi di business
    });

  }


  // Progress linea fasi: width in px (non percentuale)
  phaseProgressPx = 0;

  constructor(
    private stateManagementService: StateManagementService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const offcanvasEl = document.getElementById('offcanvasRight');
    if (offcanvasEl) {
      this.offcanvasInstance = Offcanvas.getOrCreateInstance(offcanvasEl);
    }

    this.stateManagementService.getSelectedFeature$().subscribe((data: SelectedFeatureState | null) => {
      if (!data) return;

      // ===== DATI =====
      this.attributes = data.site;
      this.documents = data.documents ?? [];
      this.documents = (data.documents ?? []).filter(d => {
        return d.wr_emission_date !== null;
      });
      this.folders = data.folders ?? [];
      this.phases = data.phases ?? [];
      this.metricCalculations = data.metricCalculations ?? [];
      this.assets = data.assets ?? [];
      this.criticals = data.criticals ?? [];

      // ===== DERIVATI =====
      this.getCriticals();

      // qui ora la fase corrente deriva dallo stato (Connesso => Fase 5)
      this.currentPhaseUi = this.getCurrentPhaseUi();

      // ===== UI =====
      this.cdr.detectChanges();
      this.offcanvasInstance?.show();

      // progress bar dopo render DOM
      setTimeout(() => this.recalcPhaseProgressPx(), 0);
    });

    this.stateManagementService.getModalOpen$().subscribe(open => {
      if (!this.offcanvasInstance) return;

      open ? this.offcanvasInstance.show() : this.offcanvasInstance.hide();
      if (open) setTimeout(() => this.recalcPhaseProgressPx(), 0);
    });

    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.map((tooltipTriggerEl: any) => new Tooltip(tooltipTriggerEl));
  }

  toggleOffcanvas(): void {
    if (!this.offcanvasInstance) return;

    const offcanvasEl = document.getElementById('offcanvasRight');
    const isShown = offcanvasEl?.classList.contains('show');

    if (isShown) {
      this.offcanvasInstance.hide();
      this.stateManagementService.setModalOpen(false);
    } else {
      this.offcanvasInstance.show();
      this.stateManagementService.setModalOpen(true);
      setTimeout(() => this.recalcPhaseProgressPx(), 0);
    }
  }

  // ======================
  // Helpers
  // ======================
  private norm(v: any): string {
    return String(v ?? '').trim().toLowerCase();
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    if (tab === 'avanzamento') setTimeout(() => this.recalcPhaseProgressPx(), 0);
  }

  getNetworkImages(): string[] {
    const raw = this.attributes?.wr_netwrk_ref;
    if (!raw) return [];

    const key = raw
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');
    return this.siteImages[key] ?? [];
  }

  // ======================
  // STATO LAVORAZIONE (wr_work_state) -> colore + label
  // ======================
  getBarClass(): string {
    const phase = phaseOfWorkState(this.attributes?.wr_work_state);
    return phase ? PHASE_BAR_CLASS[phase] : 'bar-neutral';
  }

  getWorkStateLabel(workState: unknown): string {
    const phase = phaseOfWorkState(workState);
    if (phase) return PHASE_LABEL[phase];
    return normalizeWorkState(workState) === 'criticità' ? 'Criticità' : 'Non specificato';
  }

  getStatusText(): string {
    const state = this.norm(this.attributes?.wr_work_state);
    return this.getWorkStateLabel(state);
  }

  // ======================
  // Criticità / donut
  // ======================
  getCriticals(): void {
    const c = this.norm(this.attributes?.wr_critical);
    this.critical =
      c === 'alta' ? 'Alta' :
        c === 'media' ? 'Media' :
          c === 'bassa' ? 'Bassa' :
            undefined;
  }

  getDonutValue(): number {
    return this.critical ? (this.DONUT_CONFIG[this.critical]?.value ?? 0) : 0;
  }

  getDonutClass(): string {
    return this.critical ? (this.DONUT_CONFIG[this.critical]?.class ?? '') : '';
  }

  getCriticalBoxClass(): string {
    switch (this.critical) {
      case 'Alta':
        return 'alert-danger-soft';
      case 'Media':
        return 'alert-warning-soft';
      case 'Bassa':
        return 'success-info';
      default:
        return 'alert-info';
    }
  }

  // ======================
  // PHASES (cronoprogramma - SOLO PRESENTAZIONE)
  // ======================
  getPhasesOrdered(): any[] {
    return [...(this.phases ?? [])]
      .filter(p => p.wr_phase_number !== 6) // <-- esclude Certificazioni
      .sort(
        (a, b) => Number(a?.wr_phase_number ?? 0) - Number(b?.wr_phase_number ?? 0)
      );
  }


  /**
   * COMPLETATA = tutte le fasi "prima" della fase corrente (derivata dallo stato).
   * 
   */
  isPhaseCompleted(phase: any): boolean {
    const current = this.getCurrentPhaseNumber();
    const n = Number(phase?.wr_phase_number ?? NaN);
    if (!Number.isFinite(n) || current == null) return false;
    return n < current;
  }

  /**
   *Fase corrente derivata da wr_work_state (Connesso => 5)
   */
  getCurrentPhaseNumber(): number | null {
    const phase = phaseOfWorkState(this.attributes?.wr_work_state);
    return phase ? PHASE_NUMBER[phase] : null;
  }

  /**
   * Badge fase: stesso numero della fase corrente, titolo preso da phases (se presente)
   */
  getCurrentPhaseUi(): { number: string; title: string } | null {
    const n = this.getCurrentPhaseNumber();
    if (!n) return null;

    const phase = this.getPhasesOrdered().find(p => Number(p?.wr_phase_number) === n);

    return {
      number: `Fase ${n}`,
      title: phase?.wr_title || 'In corso',
    };
  }

  /**
   * Barra segmentata: numero di segmenti attivi = fase corrente
   */
  getActiveSegments(): number {
    const n = this.getCurrentPhaseNumber();
    return n ?? 0;
  }

  // ======================
  // Progress bar fasi (linea verde in px)
  // ======================
  recalcPhaseProgressPx(): void {
    const container = document.querySelector('.phase-timeline') as HTMLElement | null;
    if (!container) return;

    const target = container.querySelector('.phase-circle.phase-next') as HTMLElement | null;

    // Se non c'è "current" nel DOM, non calcoliamo
    if (!target) {
      this.phaseProgressPx = 0;
      this.cdr.detectChanges();
      return;
    }

    this.phaseProgressPx = this.computePxToCircleCenter(container, target);
    this.cdr.detectChanges();
  }

  private computePxToCircleCenter(container: HTMLElement, circle: HTMLElement): number {
    const cRect = container.getBoundingClientRect();
    const rRect = circle.getBoundingClientRect();
    const centerX = (rRect.left - cRect.left) + (rRect.width / 2);
    return Math.max(0, Math.round(centerX));
  }
  emissionDate(d: any): boolean {
    const result =
      d?.wr_emission_date !== null &&
      d?.wr_emission_date !== undefined &&
      d?.wr_emission_date !== '';
    return result;
  }

}
